import classNames from 'classnames';
import type { Column } from 'react-table';
import React, { useState, useEffect, useMemo, useCallback } from 'react';

import Table from 'components/common/table';
import PopOver from 'components/common/popover';
import Empty from 'components/common/empty/Empty';
import CalendarCross from 'components/common/icons';
import Accordion from 'components/common/accordion';
import TableHeader from 'components/common/table/TableHeader';

import { fillMissingDates } from 'utils/roster';
import { handleError } from 'utils/errorHandler';
import { getCurrentWeekRange, getFormattedDate } from 'utils/date';

import { useMyRoleQuery } from 'hooks/query/useMyRole';
import { useCurrentUser } from 'hooks/query/useUserInfo';
import useScrollDirection from 'hooks/useScrollDirection';
import { useFetchMyTeamQuery } from 'hooks/query/useTeamQueries';

import * as mixpanel from 'services/mixPanel';

import { leaveStatus } from 'constants/leave';
import { mixPanelEvents } from 'constants/mixPanel';
import { LEAVE_STATUS_COLORS } from 'constants/leavePlan';
import { WORK_LOCATION_SHORT, workShiftIcon } from 'constants/worklog';
import { ddd_MMM_DD, ROLE, SCROLL_DIRECTION } from 'constants/appConstants';
import { MY_TEAM_FILTERS, MY_TEAM_LABELS, MY_TEAM_SORT_BY, RELATION_LABELS, WEEK_DAY_INITIALS } from 'constants/myTeam';

import { UiExclamationCircle } from '@vyaguta/vyaguta-icons/ui';

import MyTeamHeader from './components/MyTeamHeader';
import RosterInfoPopup from './components/RosterInfoPopup';
import PlannedLeavesInfo from './components/PlannedLeavesInfo';
import ActionCellContent from './components/ActionCellContent';
import AttendanceCellContent from './components/AttendanceCellContent';
import AllocationCellContent from './components/AllocationCellContent';
import TeamMemberClockInHistoryModal from './components/TeamMemberClockInHistoryModal';

import * as memory from 'utils/memory';

import { MY_TEAM_ACCORDION_STATE } from 'constants/memory';

import { getMyTeamCount, mergeGroupsForDisplay, sortAndFilterTeamGroups, withTableMetadata } from './utils/grouping';
import {
  getGroupAccordionKey,
  resolveOpenState,
  applyToggle,
  applyBulk,
  type AccordionStateMap,
} from './utils/accordionPersistence';

import type {
  ClockInHistoryMember,
  MyTeamProps,
  MyTeamRow,
  TeamAllocationItem,
  TeamGroup,
  TeamMember,
  TeamSummaryResponse,
  TeamUser,
} from './types';

/**
 * Allocations where the viewer is the appraiser — gates and feeds the Change Appraiser modal.
 * For project-scoped groups, also requires the allocation's project to match the group's project.
 */
const allocationsAsAppraiser = (
  member: TeamMember,
  viewerId?: number,
  scopeProjectId?: string | number
): TeamAllocationItem[] =>
  (member.allocation?.allocations ?? []).filter(
    (allocation) =>
      allocation.appraiserId != null &&
      String(allocation.appraiserId) === String(viewerId) &&
      (scopeProjectId == null ||
        (allocation.projectId != null && String(allocation.projectId) === String(scopeProjectId)))
  );

const MyTeam: React.FC<MyTeamProps> = (_props) => {
  const [openAccordionIds, setOpenAccordionIds] = useState<number[] | null>(null);
  const [historyMember, setHistoryMember] = useState<ClockInHistoryMember | null>(null);

  const authUser = useCurrentUser();

  const { data: authUserRoles = [] } = useMyRoleQuery(authUser?.id ? Number(authUser.id) : undefined);

  const isTeamManager = authUserRoles.includes(ROLE.TEAM_MANAGER);
  const hasViewClockInHistoryAccess =
    authUserRoles.includes(ROLE.SUPER_ADMIN) ||
    authUserRoles.includes(ROLE.HR) ||
    authUserRoles.includes(ROLE.HR_ADMIN);

  const teamSummaryPayload = useMemo(() => ({ includeFutureLeaves: true }), []);

  const {
    data: myTeamData,
    isLoading: isDataLoading,
    isError: isTeamSummaryError,
    error: teamSummaryError,
  } = useFetchMyTeamQuery(teamSummaryPayload);

  const typedTeamSummaryResult = myTeamData as TeamSummaryResponse['data'];
  const groupedData = useMemo<TeamGroup[]>(
    () => (Array.isArray(typedTeamSummaryResult?.groups) ? typedTeamSummaryResult.groups : []),
    [typedTeamSummaryResult]
  );

  const myTeamCount = getMyTeamCount(groupedData);

  const displayGroups: TeamGroup[] = useMemo<TeamGroup[]>(
    () => sortAndFilterTeamGroups(mergeGroupsForDisplay(groupedData)),
    [groupedData]
  );

  // Show the Action column for appraisees of the viewer:
  //   - in the scope-less Appraiser group only (not co-appraiser/leave-issuer),
  //   - in any area-scoped group (e.g. Team Manager) where the appraiser matches, or
  //   - in any project-scoped group whose project matches one of the viewer's appraisee allocations.
  const showAction = useMemo(
    () =>
      displayGroups.some((group) => {
        if (!group.scope) {
          return (
            group.relationType === MY_TEAM_FILTERS.APPRAISER &&
            group.members.some((member) => allocationsAsAppraiser(member, authUser?.id).length > 0)
          );
        }

        if (group.scope.type === 'project') {
          return group.members.some(
            (member) => allocationsAsAppraiser(member, authUser?.id, group.scope?.id).length > 0
          );
        }

        // area scope (e.g. Team Manager) — show if the appraiser matches
        return group.members.some((member) => allocationsAsAppraiser(member, authUser?.id).length > 0);
      }),
    [displayGroups, authUser?.id]
  );

  useEffect(() => {
    if (isTeamSummaryError && teamSummaryError) {
      handleError(teamSummaryError);
    }
  }, [isTeamSummaryError, teamSummaryError]);

  const handleScrollToBottom = () => mixpanel.trackEvent(mixPanelEvents.scroll.myTeamDashboard.toScrollBottom);
  const handleScrollToDown = () => mixpanel.trackEvent(mixPanelEvents.scroll.myTeamDashboard.toScrollDown);
  const handleScrollUp = () => mixpanel.trackEvent(mixPanelEvents.scroll.myTeamDashboard.toScrollUp);

  useScrollDirection('.my-team__table-container .rt-tbody', SCROLL_DIRECTION.BOTTOM, handleScrollToBottom);
  useScrollDirection('.my-team__table-container .rt-tbody', SCROLL_DIRECTION.DOWN, handleScrollToDown);
  useScrollDirection('.my-team__table-container .rt-tbody', SCROLL_DIRECTION.UP, handleScrollUp);

  const currentDayMonthDate = getFormattedDate(getFormattedDate(), ddd_MMM_DD);

  const handleViewFullClockInHistory = useCallback((user: TeamUser) => {
    setHistoryMember({ id: user.id, fullname: user.fullname });
  }, []);

  const columns = useMemo<Array<Column<MyTeamRow>>>(
    () => [
      {
        accessor: 'name',
        width: 300,
        sortable: false,
        resizable: false,
        Cell: (props) => (
          <AttendanceCellContent
            original={props.original}
            onViewFullClockInHistory={handleViewFullClockInHistory}
            isTeamManager={isTeamManager}
            hasViewClockInHistoryAccess={hasViewClockInHistoryAccess}
          />
        ),
      },
      {
        Header: <TableHeader label={MY_TEAM_LABELS.WEEKLY_ROSTER} identifier="roster" />,
        accessor: 'weeklyRoster',
        sortable: false,
        resizable: false,
        width: 230,
        minWidth: 230,
        maxWidth: 230,

        Cell: (props) => {
          const rosterData = props.original.attendance?.roster?.data;
          const rosterExpiry = props.original.attendance?.roster?.expiry;
          const [weekStart, weekEnd] = getCurrentWeekRange(getFormattedDate());

          const alignedRoster = fillMissingDates(rosterData, weekStart, weekEnd);

          return (
            <div className="my-team__roster-row">
              {alignedRoster.map((day, index) => (
                <div key={day.workDate} className="d-flex flex-column align-items-center gap-px-3">
                  <PopOver
                    interactive
                    useContext={false}
                    trigger="mouseenter"
                    position="bottom"
                    theme="light"
                    customClass="hpx-121 wpx-166"
                    popperOptions={{
                      modifiers: {
                        flip: {
                          enabled: true,
                          behavior: ['bottom', 'top'],
                        },
                        preventOverflow: { enabled: false },
                      },
                    }}
                    html={<RosterInfoPopup day={day} index={index} rosterExpiry={rosterExpiry} />}
                  >
                    <div className="my-team__roster-icon cursor-pointer">
                      {day.workShift ? (
                        workShiftIcon[day.workShift]
                      ) : (
                        <UiExclamationCircle size={14} color={'#999999'} />
                      )}
                    </div>
                  </PopOver>

                  <div
                    className={classNames('my-team__roster-bar wpx-22 hpx-4', {
                      'my-team__roster-bar--wfh': day.location === WORK_LOCATION_SHORT.HOME,
                      'my-team__roster-bar--wfo': day.location === WORK_LOCATION_SHORT.OFFICE,
                    })}
                  />
                </div>
              ))}
            </div>
          );
        },
      },
      {
        Header: <TableHeader label={MY_TEAM_LABELS.ALLOCATION_PER_WEEK} identifier="allocation" />,
        accessor: 'allocations',
        width: 260,
        sortable: false,
        resizable: false,
        Cell: (props) => {
          if (Object.keys(props.original || {}).length === 0) {
            return null;
          }
          const allocationData = props.original.allocation;
          const totalHours = Number(props.original.allocation?.totalAllocationHours || 0);

          return <AllocationCellContent allocationData={allocationData} totalHours={totalHours} />;
        },
      },
      {
        Header: <TableHeader label={MY_TEAM_LABELS.PLANNED_LEAVE} identifier="plannedLeaves" />,
        accessor: MY_TEAM_SORT_BY.PLANNED_LEAVES,
        minWidth: 145,
        maxWidth: 9999,
        sortable: false,
        resizable: false,
        Cell: (props) => {
          const plannedLeavesCount = props.original.attendance?.plannedFutureLeaves?.reduce(
            (total, leave) => total + leave.leaveDays,
            0
          );

          const isAnyLeavePending = props.original.attendance?.plannedFutureLeaves?.some(
            (leave) => leave.leaveStatus === leaveStatus.REQUESTED.toUpperCase()
          );

          const calendarCrossColor = isAnyLeavePending ? LEAVE_STATUS_COLORS.REQUESTED : LEAVE_STATUS_COLORS.DEFAULT;

          return (
            <div className="my-team__table-text">
              <PopOver
                interactive
                trigger="click"
                theme="light"
                useContext={false}
                html={<PlannedLeavesInfo plannedLeaves={props.original.attendance?.plannedFutureLeaves} />}
              >
                {plannedLeavesCount ? (
                  <div
                    className={classNames('attendance__planned-leaves d-flex align-items-center', {
                      'attendance__pending-color-theme': isAnyLeavePending,
                      'attendance__approved-color-theme': !isAnyLeavePending,
                    })}
                    onClick={() => mixpanel.trackEvent(mixPanelEvents.view.plannedLeaves)}
                  >
                    <span className="attendance__data-count">{plannedLeavesCount}</span>
                    <span className="attendance__leave-status d-flex" data-cy="attendance-leave-status">
                      <CalendarCross size={12} color={calendarCrossColor} />
                    </span>
                  </div>
                ) : null}
              </PopOver>
            </div>
          );
        },
      },
      ...(showAction
        ? [
            {
              id: 'action',
              Header: <TableHeader label="" identifier="action" />,
              width: 80,
              sortable: false,
              resizable: false,
              filterable: false,
              className: 'cursor-default',
              Cell: (props) => {
                const { relationType, scopeType, scopeProjectId } = props.original;

                const eligible =
                  (!scopeType && relationType === MY_TEAM_FILTERS.APPRAISER) ||
                  scopeType === 'project' ||
                  scopeType === 'area';

                return (
                  <ActionCellContent
                    filteredAllocation={
                      eligible ? allocationsAsAppraiser(props.original, authUser?.id, scopeProjectId) : []
                    }
                  />
                );
              },
            },
          ]
        : []),
    ],
    [handleViewFullClockInHistory, isTeamManager, hasViewClockInHistoryAccess, showAction, authUser?.id]
  );

  const accordionData = useMemo(
    () =>
      displayGroups.map((group) => {
        const { relationType, scope, members } = group;

        const tableData = withTableMetadata(members, relationType, scope);

        const label = RELATION_LABELS[relationType] ?? relationType;

        const title = (
          <div className="my-team__accordion-title">
            <div className="my-team__accordion-name">
              <span className="accordion-item__icon" />
              <div className="my-team__accordion-detail">
                {scope?.name ? (
                  <>
                    <div className="my-team__accordion-label">{label}</div>
                    <div className="my-team__accordion-scope">{scope.name}</div>
                  </>
                ) : (
                  <span className="my-team__accordion-scope">{label}</span>
                )}
              </div>
            </div>

            <div className="d-flex align-items-center gap-px-8">
              {WEEK_DAY_INITIALS.map((dayInitial, dayIndex) => (
                <div key={`${dayInitial}-${dayIndex}`} className="my-team__weekday">
                  {dayInitial}
                </div>
              ))}
            </div>
          </div>
        );

        return {
          title,
          content: () => (
            <div>
              <Table
                columns={columns}
                data={tableData}
                showPagination={false}
                resizable={false}
                customClassName="rt-cursor-default table-hide-header"
              />
            </div>
          ),
        };
      }),
    [displayGroups, columns]
  );

  const allAccordionIds = useMemo(() => accordionData.map((_, index) => index), [accordionData]);

  // groupIds: stable keys per group
  const groupIds = useMemo(
    () => displayGroups.map(({ relationType, scope }) => getGroupAccordionKey(relationType, scope)),
    [displayGroups]
  );

  // storedAccordionState: re-reads localStorage when groups changes
  const storedAccordionState = useMemo<AccordionStateMap>(
    () => memory.get(MY_TEAM_ACCORDION_STATE, true, {}) as AccordionStateMap,
    [groupIds]
  );

  // effectiveOpenAccordionIds: set priority(user's explicit choice > persisted state > default open)
  const effectiveOpenAccordionIds = useMemo(() => {
    if (openAccordionIds !== null) {
      return openAccordionIds.filter((id) => id < allAccordionIds.length);
    }
    const resolved = resolveOpenState(groupIds, storedAccordionState);

    return allAccordionIds.filter((index) => resolved[groupIds[index]] !== false);
  }, [openAccordionIds, allAccordionIds, groupIds, storedAccordionState]);

  const isAnyAccordionOpen = effectiveOpenAccordionIds.length > 0;
  const allCollapsed = effectiveOpenAccordionIds.length === 0;

  // Persist on toggle: read fresh, apply delta, write back
  const handleAccordionOpenStateChange = useCallback(
    (ids: number[]) => {
      setOpenAccordionIds(ids);
      const prevMap = memory.get(MY_TEAM_ACCORDION_STATE, true, {}) as AccordionStateMap;
      const openSet = new Set(ids);
      const nextMap = groupIds.reduce<AccordionStateMap>(
        (map, gid, idx) => applyToggle(map, gid, openSet.has(idx)),
        prevMap
      );

      memory.set(MY_TEAM_ACCORDION_STATE, nextMap);
    },
    [groupIds]
  );

  // Persist on expand/collapse all
  const handleToggleAllAccordions = useCallback(() => {
    setOpenAccordionIds((previousOpenIds) => {
      const previousIds = previousOpenIds === null ? allAccordionIds : previousOpenIds;
      const nextIds = previousIds.length > 0 ? [] : allAccordionIds;
      const isOpen = nextIds.length > 0;
      const prevMap = memory.get(MY_TEAM_ACCORDION_STATE, true, {}) as AccordionStateMap;

      memory.set(MY_TEAM_ACCORDION_STATE, applyBulk(prevMap, groupIds, isOpen));

      return nextIds;
    });
  }, [allAccordionIds, groupIds]);

  return (
    <div className="my-team__table-container">
      <MyTeamHeader
        myTeamCount={myTeamCount}
        currentDayMonthDate={currentDayMonthDate}
        isAnyAccordionOpen={isAnyAccordionOpen}
        allCollapsed={allCollapsed}
        displayGroups={displayGroups}
        onToggleAllAccordions={handleToggleAllAccordions}
      />
      <div className="my-team__horizontal-scroll">
        <div className="my-team__content">
          <Table
            loading={isDataLoading}
            columns={columns}
            data={[{}] as MyTeamRow[]}
            showPagination={false}
            resizable={false}
            headerOnly={true}
            customClassName="rt-cursor-default table-master-header"
            getTheadThProps={() => ({
              style: {
                display: 'flex',
                alignItems: 'center',
                height: 36,
              },
            })}
          />

          {!isDataLoading &&
            (accordionData.length > 0 ? (
              <Accordion
                data={accordionData}
                className="my-team__accordion"
                defaultAllOpen
                allowMultipleOpen
                openAccordionIds={effectiveOpenAccordionIds}
                onOpenStateChange={handleAccordionOpenStateChange}
              />
            ) : (
              <div className="my-team__empty-fallback">
                <Empty />
              </div>
            ))}
        </div>
      </div>

      <TeamMemberClockInHistoryModal
        isOpen={Boolean(historyMember)}
        member={historyMember}
        onClose={() => setHistoryMember(null)}
      />
    </div>
  );
};

export default MyTeam;
